import { FallbackErrorBoundary } from "@/components/FallbackErrorBoundary";
import { useGrouppedAppointments } from "@/features/appointments/useGrouppedAppointments";
import { groupHistoryEntries } from "@/features/history/utils";
import { useModals } from "@/features/modals/hooks";
import { SectionItemEmptyState } from "@/features/tasks/SectionItem";
import {
  type ExtendedAppointment,
  type Opportunity,
  type ParentTaskType,
  type Task,
} from "@repo/api";
import { useHistory, useTasks } from "@repo/api";
import { ListView } from "@repo/skapa";
import { t } from "@repo/translation";
import { hasItems } from "@repo/utils";

import { isAppointment } from "../appointments/utils";
import { UpcomingAppointmentItem } from "./UpcomingAppointmentItem";
import { UpcomingTaskItem } from "./UpcomingTaskItem";
import { isDropinTask } from "./isDropinTask";
import { type TaskModalContext } from "./taskModal/TaskModalReducer";

interface TaskEntry {
  timeStamp: number | undefined;
  type: "appointment" | "task";
  parentType?: ParentTaskType;
  subType?: string;
  appointment?: ExtendedAppointment;
  task?: Task;
  onClick?: () => void;
}

const normalizeEntry = (
  entry: Task | ExtendedAppointment,
  onClick?: (...args: any[]) => any
): TaskEntry => {
  if (isAppointment(entry)) {
    return {
      timeStamp: new Date(entry.timeWindowStartTime).getTime(),
      appointment: entry,
      type: "appointment",
    };
  } else {
    return {
      timeStamp: entry.taskStartDateTime
        ? new Date(entry.taskStartDateTime).getTime()
        : undefined,
      task: entry,
      type: "task",
      parentType: entry.parentTaskType,
      subType: entry.subTaskType,
      onClick,
    };
  }
};

const filterOnCompleted = (task: Task) => {
  const now = new Date().getTime();
  if (!task.isDone) {
    return true;
  }

  if (!task.completed?.taskCompletedDateTime) {
    return true;
  }

  const completedDate = new Date(task.completed.taskCompletedDateTime);
  return now - completedDate.getTime() < 1000 * 30;
};

interface TasksProps {
  opportunity: Opportunity;
  editable?: boolean;
  context: TaskModalContext;
}

export const Tasks = ({ opportunity, editable, context }: TasksProps) => {
  const { data: tasks = [] } = useTasks({
    opportunityId: String(opportunity.id),
  });

  const { upcoming } = useGrouppedAppointments(opportunity.id);

  const { data: histories } = useHistory(opportunity.id);
  const entries = groupHistoryEntries(histories ?? []);

  const { pushModal } = useModals();

  const normalizedEntries: TaskEntry[] = tasks
    .filter(filterOnCompleted)
    .filter((task) => !isDropinTask(task.parentTaskType, task.subTaskType))
    .map((task) =>
      normalizeEntry(task, () =>
        pushModal({
          type: "TASK",
          task,
          mode: "VIEW",
          historyEntries: entries.find(
            (history) => history.groupId === `TASK_${task.id}`
          )?.prevEntries,
          context,
        })
      )
    )
    .concat(upcoming.map((appointment) => normalizeEntry(appointment)));

  const sortedTasks = hasItems(normalizedEntries)
    ? normalizedEntries.sort((a, b) => {
        if (a.timeStamp === undefined) {
          return -1;
        }
        if (b.timeStamp === undefined) {
          return 1;
        }
        return a.timeStamp > b.timeStamp ? 1 : -1;
      })
    : [];

  if (!hasItems(sortedTasks)) {
    return <SectionItemEmptyState title={t("prompt.no_tasks")} />;
  }
  return (
    <ListView id="upcoming-tasks-list">
      {sortedTasks.map((entry, index) => {
        if (entry.type === "appointment" && entry.appointment) {
          return (
            <FallbackErrorBoundary
              key={entry.appointment.id}
              errorMessage={`Failed to render appointment: ${entry.appointment.id}`}
            >
              <UpcomingAppointmentItem
                editable={editable}
                key={entry.appointment.id}
                first={index === 0}
                last={index === sortedTasks.length - 1}
                appointment={entry.appointment}
                projectId={opportunity.id}
              />
            </FallbackErrorBoundary>
          );
        } else if (entry.type === "task" && entry.task) {
          return (
            <FallbackErrorBoundary
              key={entry.task.id}
              errorMessage={`Failed to render task: ${entry.task.id}`}
            >
              <UpcomingTaskItem
                editable={editable}
                task={entry.task}
                onClick={entry.onClick}
              />
            </FallbackErrorBoundary>
          );
        }
      })}
    </ListView>
  );
};
